/* =====================================================
   SIMI — behaviour layer
   Ported from the Next.js client components. Everything here is
   progressive enhancement: the markup renders and reads fine without it.

   The only scroll listener lives in initChromeSolidify, which fades the
   homepage ticker + nav from transparent to the solid paper/ink bar once
   the reader scrolls past the hero.
   ===================================================== */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------
     Nav — mobile drawer + accordion sub-menus
     --------------------------------------------------- */
  function initNav(root) {
    var openBtn = root.querySelector('[data-simi-menu-open]');
    var drawer = root.querySelector('[data-simi-drawer]');
    var overlay = root.querySelector('[data-simi-overlay]');
    var closeBtn = root.querySelector('[data-simi-menu-close]');
    if (!openBtn || !drawer || !overlay) return;

    function setOpen(open) {
      drawer.classList.toggle('is-open', open);
      overlay.classList.toggle('is-open', open);
      openBtn.setAttribute('aria-expanded', String(open));
      if (!open) {
        // collapse every sub-menu on close, matching source behaviour
        root.querySelectorAll('.simi-nav__mobile-section.is-open').forEach(function (s) {
          s.classList.remove('is-open');
          var t = s.querySelector('[data-simi-mobile-toggle]');
          if (t) {
            t.setAttribute('aria-expanded', 'false');
            var icon = t.querySelector('.simi-nav__mobile-icon');
            if (icon) icon.textContent = '+';
          }
        });
      }
    }

    openBtn.addEventListener('click', function () {
      setOpen(true);
    });
    if (closeBtn) closeBtn.addEventListener('click', function () {
      setOpen(false);
    });
    overlay.addEventListener('click', function () {
      setOpen(false);
    });
    drawer.querySelectorAll('a').forEach(function (a) {
      a.addEventListener('click', function () {
        setOpen(false);
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') setOpen(false);
    });

    // one sub-menu open at a time; glyph is + / − (U+2212)
    root.querySelectorAll('[data-simi-mobile-toggle]').forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var section = toggle.closest('.simi-nav__mobile-section');
        if (!section) return;
        var willOpen = !section.classList.contains('is-open');
        root.querySelectorAll('.simi-nav__mobile-section.is-open').forEach(function (s) {
          if (s === section) return;
          s.classList.remove('is-open');
          var t = s.querySelector('[data-simi-mobile-toggle]');
          if (t) {
            t.setAttribute('aria-expanded', 'false');
            var i = t.querySelector('.simi-nav__mobile-icon');
            if (i) i.textContent = '+';
          }
        });
        section.classList.toggle('is-open', willOpen);
        toggle.setAttribute('aria-expanded', String(willOpen));
        var icon = toggle.querySelector('.simi-nav__mobile-icon');
        if (icon) icon.textContent = willOpen ? '−' : '+';
      });
    });
  }

  /* ---------------------------------------------------
     Header search — live predictive results
     Desktop: the search icon opens a dropdown panel; typing fetches
     Shopify's predictive-search JSON and renders best-match products.
     The drawer variant has no toggle — its input is always visible.
     Without JS the icon stays a link to /search and the form submits
     to the search page normally.
     --------------------------------------------------- */
  function initSearch(root) {
    var toggle = root.querySelector('[data-simi-search-toggle]');
    var input = root.querySelector('[data-simi-search-input]');
    var results = root.querySelector('[data-simi-search-results]');
    if (!input || !results) return;

    var endpoint = (root.getAttribute('data-simi-search-url') || '/search/suggest') + '.json';
    var searchPage = root.getAttribute('data-simi-search-page') || '/search';
    var currency = root.getAttribute('data-simi-search-currency') || '';
    var debounce = null;
    var controller = null;

    function setOpen(open) {
      root.classList.toggle('is-open', open);
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
      if (open) input.focus();
    }

    if (toggle) {
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        setOpen(!root.classList.contains('is-open'));
      });
      document.addEventListener('click', function (e) {
        if (!root.contains(e.target)) setOpen(false);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && root.classList.contains('is-open')) {
          setOpen(false);
          toggle.focus();
        }
      });
    }

    function money(price) {
      return currency + String(price).replace(/\.00$/, '');
    }

    function thumb(url) {
      return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=120';
    }

    function render(products, q) {
      results.textContent = '';
      if (!products.length) {
        var empty = document.createElement('p');
        empty.className = 'simi-quicksearch__empty';
        empty.textContent = 'No results for “' + q + '”';
        results.appendChild(empty);
        return;
      }
      products.forEach(function (p) {
        var item = document.createElement('a');
        item.className = 'simi-quicksearch__item';
        item.href = p.url;
        if (p.featured_image && p.featured_image.url) {
          var img = document.createElement('img');
          img.className = 'simi-quicksearch__thumb';
          img.src = thumb(p.featured_image.url);
          img.alt = '';
          img.loading = 'lazy';
          item.appendChild(img);
        }
        var meta = document.createElement('span');
        meta.className = 'simi-quicksearch__meta';
        var title = document.createElement('span');
        title.className = 'simi-quicksearch__title';
        title.textContent = p.title;
        meta.appendChild(title);
        if (p.price != null) {
          var price = document.createElement('span');
          price.className = 'simi-quicksearch__price';
          price.textContent = money(p.price);
          meta.appendChild(price);
        }
        item.appendChild(meta);
        results.appendChild(item);
      });
      var all = document.createElement('a');
      all.className = 'simi-quicksearch__all';
      all.href = searchPage + '?q=' + encodeURIComponent(q);
      all.textContent = 'View all results';
      results.appendChild(all);
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      if (debounce) clearTimeout(debounce);
      if (controller) controller.abort();
      if (q.length < 2) {
        results.textContent = '';
        return;
      }
      debounce = setTimeout(function () {
        controller = window.AbortController ? new AbortController() : null;
        var url = endpoint +
          '?q=' + encodeURIComponent(q) +
          '&resources[type]=product' +
          '&resources[limit]=6' +
          '&resources[options][unavailable_products]=last';
        fetch(url, controller ? { signal: controller.signal } : undefined)
          .then(function (r) { return r.json(); })
          .then(function (data) {
            if (q !== input.value.trim()) return; // stale response
            var products =
              (((data || {}).resources || {}).results || {}).products || [];
            render(products, q);
          })
          .catch(function () { /* aborted or offline — keep what's shown */ });
      }, 250);
    });
  }

  /* ---------------------------------------------------
     Bestsellers carousel
     Arrows appear on hover only, and only when there is
     somewhere to scroll. Scrolls two cards at a time.
     --------------------------------------------------- */
  function initCarousel(root) {
    var track = root.querySelector('[data-simi-carousel-track]');
    var prev = root.querySelector('[data-simi-carousel-prev]');
    var next = root.querySelector('[data-simi-carousel-next]');
    if (!track || !prev || !next) return;

    var hovered = false;

    function update() {
      var atStart = track.scrollLeft <= 1;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
      var showPrev = hovered && !atStart;
      var showNext = hovered && !atEnd;
      prev.classList.toggle('is-visible', showPrev);
      next.classList.toggle('is-visible', showNext);
      prev.tabIndex = showPrev ? 0 : -1;
      next.tabIndex = showNext ? 0 : -1;
    }

    function scrollByCards(dir) {
      var first = track.firstElementChild;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 16;
      var step = first ? first.offsetWidth + gap : track.clientWidth * 0.8;
      track.scrollBy({ left: dir * step * 2, behavior: 'smooth' });
    }

    root.addEventListener('mouseenter', function () {
      hovered = true;
      update();
    });
    root.addEventListener('mouseleave', function () {
      hovered = false;
      update();
    });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    prev.addEventListener('click', function () {
      scrollByCards(-1);
    });
    next.addEventListener('click', function () {
      scrollByCards(1);
    });
    update();
  }

  /* ---------------------------------------------------
     Collections showcase
     Desktop: hover/focus/click swaps the image.
     Mobile only: auto-rotates every 3s (unless reduced motion).
     --------------------------------------------------- */
  function initCollections(root) {
    var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-simi-collection-btn]'));
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-simi-collection-slide]'));
    if (!buttons.length || !slides.length) return;

    var active = 0;
    var timer = null;
    var mobile = window.matchMedia('(max-width: 768px)');

    function setActive(i) {
      active = i;
      buttons.forEach(function (b, n) {
        b.classList.toggle('is-active', n === i);
        b.setAttribute('aria-pressed', String(n === i));
      });
      slides.forEach(function (s, n) {
        s.classList.toggle('is-active', n === i);
        s.setAttribute('aria-hidden', String(n !== i));
      });
    }

    buttons.forEach(function (btn, i) {
      ['mouseenter', 'focus', 'click'].forEach(function (evt) {
        btn.addEventListener(evt, function () {
          setActive(i);
        });
      });
    });

    function syncAutoRotate() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      if (mobile.matches && !reduceMotion.matches) {
        timer = setInterval(function () {
          setActive((active + 1) % slides.length);
        }, 3000);
      }
    }
    mobile.addEventListener('change', syncAutoRotate);
    reduceMotion.addEventListener('change', syncAutoRotate);
    syncAutoRotate();
    setActive(0);
  }

  /* ---------------------------------------------------
     FAQ — single-open accordion, first item open on load.
     Height animates via grid-template-rows 0fr -> 1fr.
     --------------------------------------------------- */
  function initFaq(root) {
    var rows = Array.prototype.slice.call(root.querySelectorAll('[data-simi-faq-row]'));
    rows.forEach(function (row) {
      row.addEventListener('click', function () {
        var isOpen = row.getAttribute('aria-expanded') === 'true';
        rows.forEach(function (r) {
          r.setAttribute('aria-expanded', 'false');
          var w = r.parentElement.querySelector('[data-simi-faq-answer]');
          if (w) w.classList.remove('is-open');
        });
        if (!isOpen) {
          row.setAttribute('aria-expanded', 'true');
          var wrap = row.parentElement.querySelector('[data-simi-faq-answer]');
          if (wrap) wrap.classList.add('is-open');
        }
      });
    });
  }

  /* ---------------------------------------------------
     PDP — accordions (single-open, all closed by default)
     --------------------------------------------------- */
  function initAccordions(root) {
    var headers = Array.prototype.slice.call(root.querySelectorAll('[data-simi-acc-header]'));
    headers.forEach(function (header) {
      header.addEventListener('click', function () {
        var acc = header.closest('.simi-acc');
        if (!acc) return;
        var willOpen = !acc.classList.contains('is-open');
        root.querySelectorAll('.simi-acc.is-open').forEach(function (a) {
          a.classList.remove('is-open');
          var p = a.querySelector('.simi-acc__plus');
          if (p) p.textContent = '+';
          var h = a.querySelector('[data-simi-acc-header]');
          if (h) h.setAttribute('aria-expanded', 'false');
        });
        acc.classList.toggle('is-open', willOpen);
        header.setAttribute('aria-expanded', String(willOpen));
        var plus = acc.querySelector('.simi-acc__plus');
        if (plus) plus.textContent = willOpen ? '−' : '+';
      });
    });
  }

  /* ---------------------------------------------------
     PDP — mobile gallery (main image + arrows + thumbs)
     --------------------------------------------------- */
  function initPdpGallery(root) {
    var imgs = Array.prototype.slice.call(root.querySelectorAll('[data-simi-gallery-img]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-simi-gallery-thumb]'));
    var prev = root.querySelector('[data-simi-gallery-prev]');
    var nextBtn = root.querySelector('[data-simi-gallery-next]');
    var thumbsWrap = root.querySelector('[data-simi-gallery-thumbs]');
    if (imgs.length < 1) return;

    var current = 0;
    function visibleImages() {
      return imgs.filter(function (img) {
        return !img.hidden;
      });
    }

    function show(i) {
      var visible = visibleImages();
      if (!visible.length) return;

      current = (i + visible.length) % visible.length;
      imgs.forEach(function (im) {
        im.classList.toggle('is-active', im === visible[current]);
      });
      thumbs.forEach(function (t) {
        t.classList.toggle(
          'is-active',
          !t.hidden && t.getAttribute('data-simi-variant-media') === visible[current].getAttribute('data-simi-variant-media')
        );
      });
    }

    function refresh() {
      var visible = visibleImages();
      var hasMultipleImages = visible.length > 1;
      if (prev) prev.hidden = !hasMultipleImages;
      if (nextBtn) nextBtn.hidden = !hasMultipleImages;
      if (thumbsWrap) thumbsWrap.hidden = !hasMultipleImages;
      show(0);
    }

    if (prev) prev.addEventListener('click', function () {
      show(current - 1);
    });
    if (nextBtn) nextBtn.addEventListener('click', function () {
      show(current + 1);
    });
    thumbs.forEach(function (t) {
      t.addEventListener('click', function () {
        var visible = visibleImages();
        var mediaId = t.getAttribute('data-simi-variant-media');
        var index = visible.findIndex(function (img) {
          return img.getAttribute('data-simi-variant-media') === mediaId;
        });
        if (index > -1) show(index);
      });
    });
    root.addEventListener('simi:variant-media-change', refresh);
    refresh();
  }

  /* ---------------------------------------------------
     PDP — variant picker
     Buttons carry their option index + value. On click we rebuild the
     selection, find the matching variant, and sync the hidden input,
     the price, and the add-to-cart state.
     --------------------------------------------------- */
  function initVariantPicker(root) {
    var dataEl = root.querySelector('[data-simi-variants]');
    var form = root.querySelector('[data-simi-product-form]');
    if (!dataEl || !form) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!Array.isArray(variants) || !variants.length) return;

    var idInput = form.querySelector('[data-simi-variant-id]');
    var priceEl = root.querySelector('[data-simi-price]');
    var addBtn = root.querySelector('[data-simi-add-btn]');
    var addLabel = root.querySelector('[data-simi-add-label]');
    var addPrice = root.querySelector('[data-simi-add-price]');
    var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-simi-option-value]'));
    var materialOptionIndex = root.hasAttribute('data-simi-material-option-index')
      ? Number(root.getAttribute('data-simi-material-option-index'))
      : null;

    // seed the selection from whatever is currently marked active
    var current = variants.find(function (v) {
      return String(v.id) === String(idInput.value);
    }) || variants[0];
    var selected = (current.options || []).slice();

    // Money formatting is Shopify's, not ours — read the pattern off the
    // server-rendered price so currency/locale stay correct.
    function formatMoney(cents) {
      var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
      var sample = priceEl ? priceEl.textContent.trim() : '';
      var symbol = sample.replace(/[\d.,\s]/g, '') || '';
      var spaced = /\s/.test(sample);
      return symbol + (spaced ? ' ' : '') + amount;
    }

    function syncVariantMedia(match) {
      if (materialOptionIndex === null || !match.options || !match.options[materialOptionIndex]) return;

      var material = match.options[materialOptionIndex];
      var materialVariantIds = variants
        .filter(function (variant) {
          return variant.options && variant.options[materialOptionIndex] === material;
        })
        .map(function (variant) {
          return String(variant.id);
        });

      root.querySelectorAll('[data-simi-variant-media]').forEach(function (media) {
        var mediaVariantIds = (media.getAttribute('data-simi-variant-ids') || '')
          .split(',')
          .filter(Boolean);
        var mediaMaterial = (media.getAttribute('data-simi-media-material') || '').toLowerCase();
        var matchesMaterial = mediaVariantIds.some(function (variantId) {
          return materialVariantIds.indexOf(variantId) > -1;
        }) || (mediaVariantIds.length === 0 && mediaMaterial === material.toLowerCase());
        media.hidden = !matchesMaterial;
      });

      // A lone visible photo spans both grid columns, and an odd count > 1
      // gets a blank pad tile; otherwise empty grid slots read as grey
      // placeholders.
      var pad = null;
      var cells = Array.prototype.slice
        .call(root.querySelectorAll('.simi-pdp__photo-cell'))
        .filter(function (cell) {
          if (cell.hasAttribute('data-simi-photo-pad')) {
            pad = cell;
            return false;
          }
          return true;
        });
      var visibleCells = cells.filter(function (cell) {
        return !cell.hidden;
      });
      cells.forEach(function (cell) {
        cell.classList.toggle('simi-pdp__photo-cell--full', visibleCells.length === 1 && !cell.hidden);
      });
      if (pad) pad.hidden = !(visibleCells.length > 1 && visibleCells.length % 2 === 1);

      var gallery = root.querySelector('[data-simi-gallery]');
      if (gallery) gallery.dispatchEvent(new CustomEvent('simi:variant-media-change'));
    }

    function syncGoldCareNotice(match) {
      if (materialOptionIndex === null || !match.options) return;

      var selectedMaterial = String(match.options[materialOptionIndex] || '').toLowerCase();
      var isGold = selectedMaterial === 'gold';
      root.querySelectorAll('[data-simi-gold-care-notice]').forEach(function (notice) {
        notice.hidden = !isGold;
      });
    }

    function sync() {
      var match = variants.find(function (v) {
        return (v.options || []).every(function (o, i) {
          return o === selected[i];
        });
      });

      buttons.forEach(function (b) {
        var i = Number(b.getAttribute('data-simi-option-index'));
        b.classList.toggle('is-active', b.getAttribute('data-simi-option-value') === selected[i]);
      });

      if (!match) {
        if (addBtn) addBtn.disabled = true;
        if (addLabel) addLabel.textContent = 'Unavailable';
        return;
      }

      idInput.value = match.id;
      var money = match.price != null ? formatMoney(match.price) : '';
      if (priceEl) priceEl.textContent = money;
      if (addPrice) addPrice.textContent = money;
      if (addBtn) addBtn.disabled = !match.available;
      if (addLabel) addLabel.textContent = match.available ? 'Add to Cart' : 'Sold Out';
      syncVariantMedia(match);
      syncGoldCareNotice(match);

      // keep the URL shareable, like Shopify's own picker
      var url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var i = Number(btn.getAttribute('data-simi-option-index'));
        selected[i] = btn.getAttribute('data-simi-option-value');
        sync();
      });
    });

    sync();
  }

  /* ---------------------------------------------------
     Collection — sort dropdown
     --------------------------------------------------- */
  function initSort(root) {
    var btn = root.querySelector('[data-simi-sort-btn]');
    if (!btn) return;
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      root.classList.toggle('is-open');
    });
    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) root.classList.remove('is-open');
    });
  }

  /* ---------------------------------------------------
     PDP — Stack It Up upsell rail
     Fills [data-simi-stack] by ranking the store catalog (/products.json):
       1. products sharing the current product's first name word
          ("Cuban Ring" pairs with "Cuban Bracelet"; leading material
          words like Gold/Silver are ignored when comparing)
       2. same product type in the opposite metal (gold <-> silver)
       3. anything under R1000
     The recommendations API is only a fallback if the catalog fetch
     fails. Each row adds straight to the cart via /cart/add.js and
     refreshes the header badge — no page reload.
     --------------------------------------------------- */
  function initStackUpsell(wrap) {
    var list = wrap.querySelector('[data-simi-stack-items]');
    var productId = wrap.getAttribute('data-simi-stack-product-id');
    if (!list || !productId) return;

    var recsUrl = wrap.getAttribute('data-simi-stack-url') || '/recommendations/products';
    var cartAddUrl = (wrap.getAttribute('data-simi-stack-cart-add-url') || '/cart/add') + '.js';
    var cartUrl = (wrap.getAttribute('data-simi-stack-cart-url') || '/cart') + '.js';
    var limit = Number(wrap.getAttribute('data-simi-stack-limit')) || 2;
    var currentTitle = wrap.getAttribute('data-simi-stack-title') || '';
    var currentType = (wrap.getAttribute('data-simi-stack-type') || '').toLowerCase().trim();
    var currentMaterial = (wrap.getAttribute('data-simi-stack-material') || '').toLowerCase().trim();

    var MATERIAL_WORDS = ['gold', 'silver', 'chrome', 'rose', 'yellow', 'white', 'platinum'];
    var OPPOSITE_MATERIAL = { gold: 'silver', silver: 'gold' };
    var BUDGET_CENTS = 100000; // R1000

    // "Silver Diamante Drop Ring" -> "diamante": drop leading material
    // words, keep the first real name word.
    function nameKey(title) {
      var words = String(title)
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter(Boolean);
      while (words.length > 1 && MATERIAL_WORDS.indexOf(words[0]) > -1) words.shift();
      return words[0] || '';
    }

    function hasMaterial(product, material) {
      return (product.variants || []).some(function (v) {
        return (
          v.available &&
          (v.options || []).some(function (o) {
            return String(o).toLowerCase().trim() === material;
          })
        );
      });
    }

    function minPrice(product) {
      var prices = (product.variants || [])
        .filter(function (v) { return v.available; })
        .map(function (v) { return v.price; });
      return prices.length ? Math.min.apply(null, prices) : Infinity;
    }

    // Same trick as the variant picker: read currency off the server-rendered
    // price so locale/currency stay Shopify's problem, not ours.
    function formatMoney(cents) {
      var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
      var priceEl = document.querySelector('[data-simi-price]');
      var sample = priceEl ? priceEl.textContent.trim() : '';
      var symbol = sample.replace(/[\d.,\s]/g, '') || '';
      var spaced = /\s/.test(sample);
      return symbol + (spaced ? ' ' : '') + amount;
    }

    function imageUrl(src, width) {
      if (!src) return '';
      return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
    }

    function refreshCartBadge() {
      fetch(cartUrl)
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          document.querySelectorAll('.simi-nav__cart-btn').forEach(function (btn) {
            var badge = btn.querySelector('.simi-nav__cart-badge');
            if (cart.item_count > 0) {
              if (!badge) {
                badge = document.createElement('span');
                badge.className = 'simi-nav__cart-badge';
                btn.appendChild(badge);
              }
              badge.textContent = cart.item_count;
            } else if (badge) {
              badge.remove();
            }
          });
        })
        .catch(function () {});
    }

    function addToCart(variantId, btn) {
      var label = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding';
      fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function () {
          btn.textContent = 'Added';
          btn.classList.add('is-added');
          refreshCartBadge();
          if (simiCart.refresh) simiCart.refresh();
          setTimeout(function () {
            btn.textContent = label;
            btn.classList.remove('is-added');
            btn.disabled = false;
          }, 1600);
        })
        .catch(function () {
          btn.textContent = 'Try again';
          setTimeout(function () {
            btn.textContent = label;
            btn.disabled = false;
          }, 1600);
        });
    }

    function renderItem(product) {
      var availableVariants = (product.variants || []).filter(function (v) {
        return v.available;
      });
      if (!availableVariants.length) return null;

      var item = document.createElement('div');
      item.className = 'simi-stack__item';

      var media = document.createElement('a');
      media.className = 'simi-stack__media';
      media.href = product.url;
      var img = document.createElement('img');
      img.src = imageUrl(product.featured_image || (product.images && product.images[0]) || '', 160);
      img.alt = product.title;
      img.width = 64;
      img.height = 64;
      img.loading = 'lazy';
      media.appendChild(img);

      var meta = document.createElement('div');
      meta.className = 'simi-stack__meta';
      var title = document.createElement('a');
      title.className = 'simi-stack__title';
      title.href = product.url;
      title.textContent = product.title;
      var price = document.createElement('div');
      price.className = 'simi-stack__price';
      price.textContent = formatMoney(availableVariants[0].price);
      meta.appendChild(title);
      meta.appendChild(price);

      var actions = document.createElement('div');
      actions.className = 'simi-stack__actions';
      var selectedId = availableVariants[0].id;

      if (availableVariants.length > 1) {
        var select = document.createElement('select');
        select.className = 'simi-stack__select';
        select.setAttribute('aria-label', 'Options for ' + product.title);
        availableVariants.forEach(function (v) {
          var opt = document.createElement('option');
          opt.value = v.id;
          opt.textContent = v.title;
          select.appendChild(opt);
        });
        select.addEventListener('change', function () {
          selectedId = select.value;
          var match = availableVariants.find(function (v) {
            return String(v.id) === String(select.value);
          });
          if (match) price.textContent = formatMoney(match.price);
        });
        actions.appendChild(select);
      }

      var addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'simi-stack__add';
      addBtn.textContent = '+ Add';
      addBtn.addEventListener('click', function () {
        addToCart(selectedId, addBtn);
      });
      actions.appendChild(addBtn);

      item.appendChild(media);
      item.appendChild(meta);
      item.appendChild(actions);
      return item;
    }

    function fetchRecs(intent) {
      var url =
        recsUrl + '.json?product_id=' + encodeURIComponent(productId) +
        '&limit=' + (limit + 1) + '&intent=' + intent;
      return fetch(url)
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (data) {
          return (data.products || []).filter(function (p) {
            return p.available && String(p.id) !== String(productId);
          });
        })
        .catch(function () { return []; });
    }

    // Normalise /products.json entries to the shape renderItem expects
    // (recommendations-API-style: url, featured_image, prices in cents).
    function fetchCatalog() {
      return fetch('/products.json?limit=250')
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (data) {
          return (data.products || [])
            .map(function (p) {
              var variants = (p.variants || []).map(function (v) {
                return {
                  id: v.id,
                  title: v.title,
                  available: v.available !== false,
                  price: Math.round(parseFloat(v.price) * 100) || 0,
                  options: [v.option1, v.option2, v.option3].filter(Boolean)
                };
              });
              return {
                id: p.id,
                title: p.title,
                url: '/products/' + p.handle,
                product_type: p.product_type || '',
                featured_image: (p.images && p.images[0] && p.images[0].src) || '',
                variants: variants,
                available: variants.some(function (v) { return v.available; })
              };
            })
            .filter(function (p) {
              return p.available && String(p.id) !== String(productId);
            });
        })
        .catch(function () { return []; });
    }

    // Priority pairing: same first name word, then same type in the
    // opposite metal, then anything under budget.
    function rankCatalog(products) {
      var key = nameKey(currentTitle);
      var opposite = OPPOSITE_MATERIAL[currentMaterial] || '';
      var chosen = [];
      var used = {};

      function take(matches) {
        products.forEach(function (p) {
          if (chosen.length >= limit || used[p.id]) return;
          if (matches(p)) {
            used[p.id] = true;
            chosen.push(p);
          }
        });
      }

      take(function (p) {
        return key && nameKey(p.title) === key;
      });
      take(function (p) {
        return (
          opposite &&
          currentType &&
          (p.product_type || '').toLowerCase().trim() === currentType &&
          hasMaterial(p, opposite)
        );
      });
      take(function (p) {
        return minPrice(p) < BUDGET_CENTS;
      });
      return chosen;
    }

    fetchCatalog()
      .then(function (products) {
        var ranked = rankCatalog(products);
        if (ranked.length) return ranked;
        return fetchRecs('complementary').then(function (recs) {
          return recs.length ? recs : fetchRecs('related');
        });
      })
      .then(function (products) {
        var rendered = 0;
        products.slice(0, limit).forEach(function (product) {
          var item = renderItem(product);
          if (item) {
            list.appendChild(item);
            rendered++;
          }
        });
        if (rendered > 0) wrap.hidden = false;
      });
  }

  /* ---------------------------------------------------
     Cart drawer
     Slide-in cart panel from the right (ported from the SIMI Next.js
     store). The header cart icon opens it instead of navigating to
     /cart; add-to-cart flows open it too. State lives in /cart.js —
     quantity changes go through /cart/change.js and re-render.
     --------------------------------------------------- */
  var simiCart = { open: null, refresh: null };

  function initCartDrawer(root) {
    var content = root.querySelector('[data-simi-cart-drawer-content]');
    var footer = root.querySelector('[data-simi-cart-drawer-footer]');
    var subtotalEl = root.querySelector('[data-simi-cart-drawer-subtotal]');
    if (!content || !footer) return;

    var cartUrl = (root.getAttribute('data-simi-cart-url') || '/cart') + '.js';
    var changeUrl = (root.getAttribute('data-simi-cart-change-url') || '/cart/change') + '.js';
    var moneySample = root.getAttribute('data-simi-money-sample') || '';
    var busy = false;

    function formatMoney(cents) {
      var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
      var symbol = moneySample.replace(/[\d.,\s]/g, '') || '';
      var spaced = /\s/.test(moneySample.trim());
      return symbol + (spaced ? ' ' : '') + amount;
    }

    function imageUrl(src, width) {
      if (!src) return '';
      return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
    }

    function updateBadge(cart) {
      document.querySelectorAll('.simi-nav__cart-btn').forEach(function (btn) {
        var badge = btn.querySelector('.simi-nav__cart-badge');
        if (cart.item_count > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'simi-nav__cart-badge';
            btn.appendChild(badge);
          }
          badge.textContent = cart.item_count;
        } else if (badge) {
          badge.remove();
        }
      });
    }

    function changeLine(key, quantity) {
      if (busy) return;
      busy = true;
      root.classList.add('is-busy');
      fetch(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          busy = false;
          root.classList.remove('is-busy');
          render(cart);
        })
        .catch(function () {
          busy = false;
          root.classList.remove('is-busy');
          refresh();
        });
    }

    function renderItem(item) {
      var row = document.createElement('div');
      row.className = 'simi-cart-drawer__item';

      if (item.image) {
        var media = document.createElement('a');
        media.className = 'simi-cart-drawer__media';
        media.href = item.url;
        var img = document.createElement('img');
        img.src = imageUrl(item.image, 160);
        img.alt = item.product_title;
        img.width = 80;
        img.height = 80;
        img.loading = 'lazy';
        media.appendChild(img);
        row.appendChild(media);
      }

      var details = document.createElement('div');
      details.className = 'simi-cart-drawer__details';

      var top = document.createElement('div');
      top.className = 'simi-cart-drawer__row';
      var title = document.createElement('a');
      title.className = 'simi-cart-drawer__product-title';
      title.href = item.url;
      title.textContent = item.product_title;
      var price = document.createElement('span');
      price.className = 'simi-cart-drawer__price';
      price.textContent = formatMoney(item.final_line_price);
      top.appendChild(title);
      top.appendChild(price);
      details.appendChild(top);

      if (item.variant_title && item.variant_title !== 'Default Title') {
        var variant = document.createElement('div');
        variant.className = 'simi-cart-drawer__variant';
        variant.textContent = item.variant_title;
        details.appendChild(variant);
      }

      var qtyRow = document.createElement('div');
      qtyRow.className = 'simi-cart-drawer__qty-row';

      var qty = document.createElement('div');
      qty.className = 'simi-cart-drawer__qty';
      var minus = document.createElement('button');
      minus.type = 'button';
      minus.className = 'simi-cart-drawer__qty-btn';
      minus.setAttribute('aria-label', 'Decrease quantity');
      minus.textContent = '−';
      minus.addEventListener('click', function () {
        changeLine(item.key, item.quantity - 1);
      });
      var value = document.createElement('span');
      value.className = 'simi-cart-drawer__qty-value';
      value.textContent = item.quantity;
      var plus = document.createElement('button');
      plus.type = 'button';
      plus.className = 'simi-cart-drawer__qty-btn';
      plus.setAttribute('aria-label', 'Increase quantity');
      plus.textContent = '+';
      plus.addEventListener('click', function () {
        changeLine(item.key, item.quantity + 1);
      });
      qty.appendChild(minus);
      qty.appendChild(value);
      qty.appendChild(plus);

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'simi-cart-drawer__remove';
      remove.textContent = 'remove';
      remove.addEventListener('click', function () {
        changeLine(item.key, 0);
      });

      qtyRow.appendChild(qty);
      qtyRow.appendChild(remove);
      details.appendChild(qtyRow);

      row.appendChild(details);
      return row;
    }

    function render(cart) {
      updateBadge(cart);
      content.innerHTML = '';

      if (!cart.items || !cart.items.length) {
        var empty = document.createElement('div');
        empty.className = 'simi-cart-drawer__empty';
        var msg = document.createElement('p');
        msg.textContent = 'Your cart is empty.';
        var continueBtn = document.createElement('button');
        continueBtn.type = 'button';
        continueBtn.className = 'simi-cart-drawer__continue';
        continueBtn.textContent = 'Continue Shopping';
        continueBtn.addEventListener('click', close);
        empty.appendChild(msg);
        empty.appendChild(continueBtn);
        content.appendChild(empty);
        footer.hidden = true;
        return;
      }

      var lines = document.createElement('div');
      lines.className = 'simi-cart-drawer__lines';
      cart.items.forEach(function (item) {
        lines.appendChild(renderItem(item));
      });
      content.appendChild(lines);

      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.items_subtotal_price);
      footer.hidden = false;
    }

    function refresh() {
      return fetch(cartUrl)
        .then(function (r) { return r.json(); })
        .then(render)
        .catch(function () {});
    }

    function open() {
      refresh();
      root.classList.add('is-open');
      root.setAttribute('aria-hidden', 'false');
      document.body.classList.add('simi-cart-drawer-open');
    }

    function close() {
      root.classList.remove('is-open');
      root.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('simi-cart-drawer-open');
    }

    simiCart.open = open;
    simiCart.refresh = function () {
      if (root.classList.contains('is-open')) refresh();
    };

    root.querySelectorAll('[data-simi-cart-drawer-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && root.classList.contains('is-open')) close();
    });
    document.querySelectorAll('.simi-nav__cart-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        open();
      });
    });
  }

  /* ---------------------------------------------------
     PDP — AJAX add to cart
     Intercepts the product form so adding opens the cart drawer
     instead of navigating to the cart page. Falls back to a normal
     form POST if the drawer is absent or the request fails.
     --------------------------------------------------- */
  function initAjaxAddForm(form) {
    form.addEventListener('submit', function (e) {
      if (!simiCart.open) return;
      var idInput = form.querySelector('[data-simi-variant-id]');
      if (!idInput) return;
      e.preventDefault();

      var btn = form.querySelector('[data-simi-add-btn]');
      var label = form.querySelector('[data-simi-add-label]');
      var prev = label ? label.textContent : '';
      if (btn) btn.disabled = true;
      if (label) label.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(idInput.value), quantity: 1 })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function () {
          if (label) label.textContent = prev;
          if (btn) btn.disabled = false;
          simiCart.open();
        })
        .catch(function () {
          if (label) label.textContent = 'Try again';
          setTimeout(function () {
            if (label) label.textContent = prev;
            if (btn) btn.disabled = false;
          }, 1600);
        });
    });
  }

  /* ---------------------------------------------------
     Homepage chrome solidify
     The ticker + nav stay visible but start transparent with
     ivory text over the hero (see .simi-chrome-solid in
     simi-sections.css), then fade to the solid paper/ink bar
     once the reader scrolls past most of the hero. Index
     template only; toggles a class on <body>, rAF-throttled.
     --------------------------------------------------- */
  function initChromeSolidify() {
    if (!document.body.classList.contains('template-index')) return;
    var hero = document.querySelector('.simi-hero');
    if (!hero) return;

    var ticking = false;
    function update() {
      ticking = false;
      var solid = window.pageYOffset >= Math.max(120, hero.offsetHeight * 0.55);
      document.body.classList.toggle('simi-chrome-solid', solid);
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update(); // set initial state (handles a reload mid-page)
  }

  /* --------------------------------------------------- */
  function boot(scope) {
    var d = scope || document;
    d.querySelectorAll('[data-simi-nav]').forEach(initNav);
    d.querySelectorAll('[data-simi-search]').forEach(initSearch);
    d.querySelectorAll('[data-simi-carousel]').forEach(initCarousel);
    d.querySelectorAll('[data-simi-collections]').forEach(initCollections);
    d.querySelectorAll('[data-simi-faq]').forEach(initFaq);
    d.querySelectorAll('[data-simi-accordions]').forEach(initAccordions);
    d.querySelectorAll('[data-simi-gallery]').forEach(initPdpGallery);
    d.querySelectorAll('[data-simi-sort]').forEach(initSort);
    d.querySelectorAll('[data-simi-stack]').forEach(initStackUpsell);
    d.querySelectorAll('.simi-pdp').forEach(initVariantPicker);
    d.querySelectorAll('[data-simi-cart-drawer]').forEach(initCartDrawer);
    d.querySelectorAll('[data-simi-product-form]').forEach(initAjaxAddForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
      initChromeSolidify();
    });
  } else {
    boot();
    initChromeSolidify();
  }

  // Re-init sections the theme editor re-renders.
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });
})();
